import axios from 'axios';
import { LoginCredentials, RegisterData, AuthResponse, User } from '../types';

// Detect current domain and use same for API
const getCurrentDomain = () => {
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}`;
  }
  return 'https://plannitas.com';
};

const API_BASE_URL = process.env.REACT_APP_API_URL || `${getCurrentDomain()}/api`;

// Helper function for fetch with credentials
const fetchWithCredentials = async (url: string, options: RequestInit = {}) => {
  const response = await fetch(`${API_BASE_URL}/auth${url}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Error de red' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

const authApi = axios.create({
  baseURL: `${API_BASE_URL}/auth`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token to requests
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await authApi.post('/refresh', { refreshToken });
          const { token, refreshToken: newRefreshToken } = response.data;
          
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', newRefreshToken);
          
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return authApi(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const authService = {
  // Login with email and password
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    return await fetchWithCredentials('/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  // Register new user
  register: async (userData: RegisterData): Promise<AuthResponse> => {
    return await fetchWithCredentials('/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  // Get current user profile
  getProfile: async (): Promise<{ user: User }> => {
    return await fetchWithCredentials('/me');
  },

  // Update user profile
  updateProfile: async (userData: Partial<User>): Promise<{ user: User }> => {
    const response = await authApi.put('/profile', userData);
    return response.data;
  },

  // Change password
  changePassword: async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
    const response = await authApi.put('/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  },

  // Refresh token
  refreshToken: async (refreshToken: string): Promise<{ token: string; refreshToken: string }> => {
    const response = await authApi.post('/refresh', { refreshToken });
    return response.data;
  },

  // Logout
  logout: async (): Promise<{ message: string }> => {
    return await fetchWithCredentials('/logout', {
      method: 'POST',
    });
  },

  // Google OAuth URL
  getGoogleAuthUrl: (): string => {
    return `${API_BASE_URL}/auth/google`;
  },

  // Check if Google OAuth is available
  isGoogleAuthAvailable: async (): Promise<boolean> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/google`);
      return response.status !== 501;
    } catch (error: any) {
      return error.response?.status !== 501;
    }
  },

  // Check auth status using cookies
  checkAuthStatus: async (): Promise<{ user: User }> => {
    try {
      return await fetchWithCredentials('/me');
    } catch (error) {
      console.error('Auth status check failed:', error);
      throw error;
    }
  },

  // Get list of active dentists
  getDentists: async (): Promise<{ dentists: Array<{ id: string; name: string; firstName: string; lastName: string }> }> => {
    return await fetchWithCredentials('/dentists');
  },

  // Store authentication data
  storeAuthData: (authData: AuthResponse): void => {
    localStorage.setItem('token', authData.token);
    localStorage.setItem('refreshToken', authData.refreshToken);
    localStorage.setItem('user', JSON.stringify(authData.user));
  },

  // Clear authentication data
  clearAuthData: (): void => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  },

  // Get stored authentication data
  getStoredAuthData: (): { token: string | null; refreshToken: string | null; user: User | null } => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    return { token, refreshToken, user };
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('token');
    return !!token;
  },

  // Change user role (admin only)
  changeUserRole: async (userId: string, newRole: string): Promise<any> => {
    const response = await authApi.put('/change-role', {
      userId,
      newRole
    });
    return response.data;
  },

  // Delete user (admin only)
  deleteUser: async (userId: string): Promise<any> => {
    const response = await authApi.delete(`/users/${userId}`);
    return response.data;
  },

  // Get all users (admin only)
  getUsers: async (): Promise<User[]> => {
    const response = await authApi.get('/users');
    return response.data;
  },

  // Register internal user (admin only)
  registerInternal: async (userData: RegisterData): Promise<any> => {
    const response = await authApi.post('/register-internal', userData);
    return response.data;
  }
};

export default authService;